import React, { useEffect, useRef } from "react";
import {
  Chart,
  ChartOptions,
  ChartData,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Plugin,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, ChartDataLabels);

interface LeadQualityBarChartProps {
  chartDatas: number[];
  backgrounds: string[];
  labels: string[];
  canvasId: string;
}

const LeadQualityBarChart: React.FC<LeadQualityBarChartProps> = ({ chartDatas, backgrounds, labels, canvasId }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null); // Store the chart instance

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");

    if (!ctx) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy(); // Cleanup previous instance
    }

    const chartData: ChartData<"bar"> = {
      labels: labels,
      datasets: [
        {
          label: "",
          data: canvasId === "performanceChart" ?
          chartDatas.map((item: any) => {
            return Number(String(item.replace(/%/g, "")));
          })
          : chartDatas,
          backgroundColor: backgrounds,
          borderColor: backgrounds,
          borderWidth: 1,
          barThickness: 40,
          borderRadius: 8,
          borderSkipped: false, // Ensure all corners get rounded
        },
      ],
    };

    const chartOptions: ChartOptions<"bar"> = {
      responsive: true,
      maintainAspectRatio: true,
      layout: {
        padding: {
          top: 20,
        },
      },
      scales: {
        x: {
          display: false,
          grid: { display: false },
          ticks: { display: false },
        },
        y: {
          display: false,
          grid: { display: false },
          ticks: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        datalabels: {
          display: true,
          align: "end",
          anchor: "end",
          color: (context) => {
            const dataset = context.dataset;
            const index = context.dataIndex;
            return Array.isArray(dataset.backgroundColor)
              ? dataset.backgroundColor[index] || "#000" // Fallback to black if undefined
              : dataset.backgroundColor || "#000"; // Handle non-array cases
        },
        font: { weight: "bold", size: 14 },
        offset: -4,
        formatter: canvasId === "performanceChart" ?  (value) => value + "%"
        : (value: number) => (value > 999 ? `${value / 1000}k` : value),
        clip: false, // Prevents labels from being cut off
      },
    },
  };

  chartInstanceRef.current = new Chart(ctx, {
    type: "bar",
    data: chartData,
    options: chartOptions,
  });

  return () => {
    chartInstanceRef.current?.destroy();
  };
}, [chartDatas, backgrounds, labels]);

return <canvas id={canvasId} ref={canvasRef}></canvas>;
};

export default LeadQualityBarChart;
